import type { IConsumable } from '@experiences/interfaces';
import { UiText } from '@experiences/ui-common';
import LaunchIcon from '@mui/icons-material/Launch';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import React, {
    useMemo,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import urljoin from 'url-join';

import { useTenantIdContext } from '../../../common/providers/TenantIdProvider';
import { getDefaultConsumptionPool } from '../../../services/licensing/LicenseUsageService';
import { accountLogicalName } from '../../../store/selectors';
import { UiChartLoading } from '../helperComponents/UiConsumableCard/UiChartLoading';
import { DailyConsumptionGraph } from './DailyConsumptionGraph';
import { MonthlyConsumptionGraph } from './MonthlyConsumptionGraph';
import { TotalConsumptionGraph } from './TotalConsumptionGraph';

interface ConsumptionGraphGroup {
    product: string;
    consumable: IConsumable;
    tenantId?: string;
    startDateParam?: number;
    endDateParam?: number;
}

interface ICheckboxGroup {
    primary: string;
    secondary: string;
}

interface IGraphComponent {
    [key: string]: { component: React.FC<any> | undefined; props: any; validate: () => boolean | undefined | number };
}

enum RadioGroupValue {
    PRIMARY = 'primary',
    SECONDARY = 'secondary',
}

const useStyles = makeStyles(theme =>
    createStyles({
        radioGroup: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'left',
            fontSize: '12px',
            fontColor: theme.palette.semantic.colorForegroundDeEmp,
            fontWeight: 400,
            paddingTop: '11px',
            paddingBottom: '16px',
            '& .MuiRadio-root': {
                paddingTop: '0px',
                paddingBottom: '0px',
            },
        },
        radioGroupLabel: {
            paddingRight: '16px',
            fontSize: '12px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        radioButtonLabel: { marginBottom: 0 },
        switchLabel: {
            display: 'flex',
            alignItems: 'center',
            fontSize: '12px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        radioGroupContainer: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
                alignItems: 'flex-start',
            },
        },
        dashboardButton: {
            fontSize: '12px',
            textTransform: 'none',
            marginBottom: '4px',
        },
        buttonsContainer: {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: '8px',
            marginTop: theme.breakpoints.down('sm') ? '8px' : '0px',
        },
    }));

export const ConsumptionGraphGroup: React.FC<ConsumptionGraphGroup> = ({
    product, consumable, tenantId, startDateParam, endDateParam,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const [ startDate, endDate ] = useMemo(() => {
        if (startDateParam && endDateParam) {
            return [ startDateParam, endDateParam ];
        }
        return getDefaultConsumptionPool(consumable);
    }, [ consumable, startDateParam, endDateParam ]);

    const [ selectedRadio, setSelectedRadio ] = React.useState<string>(RadioGroupValue.PRIMARY.valueOf());
    const { tenantName: contextTenantName } = useTenantIdContext();
    const orgName = useSelector(accountLogicalName);

    const [ translateMap ] = useState<Map<string, ICheckboxGroup>>(
        new Map([
            [
                'AIU',
                {
                    primary: 'CLIENT_CONSUMABLE_TOTAL_CONSUMPTION_PLAN',
                    secondary: 'CLIENT_CONSUMABLE_MONTLHY_CONSUMPTION',
                },
            ],
            [
                'PLTU',
                {
                    primary: 'CLIENT_CONSUMABLE_TOTAL_CONSUMPTION_PLAN',
                    secondary: '',
                },
            ],
            [
                'AGU',
                {
                    primary: 'CLIENT_CONSUMABLE_TOTAL_CONSUMPTION_PLAN',
                    secondary: '',
                },
            ],
            [
                'RU',
                {
                    primary: 'CLIENT_CONSUMABLE_MONTLHY_CONSUMPTION',
                    secondary: 'CLIENT_CONSUMABLE_SERVERLESS_CURRENT_MONTH',
                },
            ],
            [
                'TEU',
                {
                    primary: 'CLIENT_CONSUMABLE_TOTAL_CONSUMPTION_PLAN',
                    secondary: '',
                },
            ],
        ]),
    );

    const getStartOfCurrentMonth = (): number => {
        const date = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
        return (date.getTime() / 1000) - date.getTimezoneOffset() * 60;
    };

    const getEndOfCurrentMonth = (): number => {
        const date = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
        return (date.getTime() / 1000) - date.getTimezoneOffset() * 60;
    };

    const productGraphComponents: Map<string, IGraphComponent> = new Map([
        [
            'AIU',
            {
                primary: {
                    component: TotalConsumptionGraph,
                    props: {
                        consumableCode: product,
                        startDate,
                        endDate,
                        tenantId,
                        chartDescription: translate({ id: `CLIENT_CONSUMABLE_TOTAL_CONSUMPTION_PLAN` }),
                    },
                    validate: () => startDate && endDate,
                },
                secondary: {
                    component: MonthlyConsumptionGraph,
                    props: {
                        consumableCode: product,
                        tenantId,
                        chartDescription: translate({ id: `CLIENT_CONSUMABLE_MONTLHY_CONSUMPTION` }),
                    },
                    validate: (): boolean => true,
                },
            },
        ],
        [
            'PLTU',
            {
                primary: {
                    component: TotalConsumptionGraph,
                    props: {
                        consumableCode: product,
                        startDate,
                        endDate,
                        tenantId,
                        chartDescription: translate({ id: `CLIENT_CONSUMABLE_TOTAL_CONSUMPTION_PLAN` }),
                    },
                    validate: () => startDate && endDate,
                },
                secondary: {
                    component: undefined,
                    props: {},
                    validate: (): boolean => true,
                },
            },
        ],
        [
            'AGU',
            {
                primary: {
                    component: TotalConsumptionGraph,
                    props: {
                        consumableCode: product,
                        startDate,
                        endDate,
                        tenantId,
                        chartDescription: translate({ id: `CLIENT_CONSUMABLE_TOTAL_CONSUMPTION_PLAN` }),
                    },
                    validate: () => startDate && endDate,
                },
                secondary: {
                    component: undefined,
                    props: {},
                    validate: (): boolean => true,
                },
            },
        ],
        [
            'RU',
            {
                primary: {
                    component: MonthlyConsumptionGraph,
                    props: {
                        consumableCode: product,
                        tenantId,
                        chartDescription: translate({ id: `CLIENT_CONSUMABLE_MONTLHY_CONSUMPTION` }),
                    },
                    validate: (): boolean => true,
                },
                secondary: {
                    component: DailyConsumptionGraph,
                    props: {
                        consumableCode: product,
                        services: [ 'serverless' ],
                        startDate: getStartOfCurrentMonth(),
                        endDate: getEndOfCurrentMonth(),
                        tenantId,
                        chartDescription: translate({ id: `CLIENT_CONSUMABLE_SERVERLESS_CURRENT_MONTH` }),
                    },
                    validate: () => startDate && endDate,
                },
            },
        ],
        [
            'TEU',
            {
                primary: {
                    component: TotalConsumptionGraph,
                    props: {
                        consumableCode: product,
                        startDate,
                        endDate,
                        tenantId,
                        chartDescription: translate({ id: `CLIENT_CONSUMABLE_TOTAL_CONSUMPTION_PLAN` }),
                    },
                    validate: () => startDate && endDate,
                },
                secondary: {
                    component: undefined,
                    props: {},
                    validate: (): boolean => true,
                },
            },
        ],
    ]) ;

    const getProductGraphComponent = () => {

        let CustomComponent: React.FC<any> = UiChartLoading;
        let props = {
            width: 150,
            height: 36,
        };

        const selectedProductGraphs = productGraphComponents.get(product);

        if (!selectedProductGraphs) {
            return <CustomComponent {...props} />;
        }

        const graphComponent = selectedProductGraphs[selectedRadio];

        if (!graphComponent.component) {
            return <CustomComponent {...props} />;
        }

        if (graphComponent.validate()) {
            CustomComponent = graphComponent.component;
            props = graphComponent.props;
        }

        return <CustomComponent {...props} />;
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedRadio((event.target as HTMLInputElement).value);
    };

    const targetTenantName = useMemo(() => {
        if (contextTenantName && contextTenantName.length > 0) {
            return contextTenantName.toLowerCase();
        }

        return window.PortalShell?.AccountAndTenants.getTenant()?.name?.toLowerCase() ?? '';
    }, [ contextTenantName ]);

    const tenantDashboardUrl = useMemo(() =>
        urljoin(
            window.location.origin,
            orgName,
            targetTenantName,
            'insights_',
            'dashboard',
            'UiPath-LI-AIU-Insights::aiu_tenant_overview'
        ),
    [ orgName, targetTenantName ]);

    const orgDashboardUrl = useMemo(() =>
        urljoin(
            window.location.origin,
            orgName,
            targetTenantName,
            'insights_',
            'dashboard',
            'UiPath-LI-AIU-Insights::aiu_organization_overview_org'
        ),
    [ orgName, targetTenantName ]);

    const showOrgDashboardLink = useMemo(() => !contextTenantName || contextTenantName.length === 0, [ contextTenantName ]);

    const showAIUDashboardsLink = useMemo(() => product === 'AIU', [ product ]);

    return (
        <>
            <div className={classes.radioGroupContainer}>
                <RadioGroup
                    aria-label={translate({ id: `CLIENT_CONSUMABLE_TIMEFRAME_COLON` })}
                    data-cy="ui-chart-section-radio-group"
                    value={selectedRadio}
                    onChange={handleChange}>
                    <div className={classes.radioGroup}>
                        <UiText className={classes.radioGroupLabel}>
                            {translate({ id: `CLIENT_CONSUMABLE_TIMEFRAME_COLON` },)}
                        </UiText>
                        <FormControlLabel
                            value={RadioGroupValue.PRIMARY}
                            control={<Radio color="primary" />}
                            label={
                                <div className={classes.switchLabel}>
                                    {translate({ id: translateMap.get(product)?.primary })}
                                </div>
                            }
                            data-cy="first-radio-consumption"
                            className={classes.radioButtonLabel}
                        />
                        {translateMap.get(product)?.secondary && (
                            <FormControlLabel
                                value={RadioGroupValue.SECONDARY}
                                control={<Radio color="primary" />}
                                label={
                                    <div className={classes.switchLabel}>
                                        {translate({ id: translateMap.get(product)?.secondary })}
                                    </div>
                                }
                                data-cy="second-radio-consumption"
                                className={classes.radioButtonLabel}
                            />
                        )}
                    </div>
                </RadioGroup>
                <div className={classes.buttonsContainer}>
                    {showAIUDashboardsLink && (
                        <>
                            {!showOrgDashboardLink && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    className={classes.dashboardButton}
                                    endIcon={<LaunchIcon />}
                                    component={Link}
                                    href={tenantDashboardUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-cy="aiu-tenant-dashboard-button"
                                >
                                    {translate({ id: 'CLIENT_VIEW_AIU_TENANT_DASHBOARD' })}
                                </Button>
                            )}
                            {showOrgDashboardLink && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    className={classes.dashboardButton}
                                    endIcon={<LaunchIcon />}
                                    component={Link}
                                    href={orgDashboardUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-cy="aiu-org-dashboard-button"
                                >
                                    {translate({ id: 'CLIENT_VIEW_AIU_ORG_DASHBOARD' })}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>
            {getProductGraphComponent()}
        </>
    );
};
